import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createOtNote,
  getOtNoteById,
  listAnaesthesiaTypes,
  listOtNotesByVisit,
  listOtResults,
  listOtReservations,
  listOtTables,
  listOtTheatres,
  mapReservationToTableRow,
  searchOtSurgeries,
  toLookupOption,
  updateOtNote,
} from "../../services/otNoteService";

const initialState = {
  activeTab: "sheet",
  selectedPatient: null,
  operationDetailsId: null,
  otReservationId: null,
  isOtReserved: false,
  theatres: [],
  tables: [],
  surgeries: [],
  anaesthesiaTypes: [],
  otResults: [],
  reservations: [],
  noteList: [],
  currentNote: null,
  loading: false,
  lookupsLoading: false,
  reservationsLoading: false,
  saving: false,
  error: null,
};

export const fetchOtReservations = createAsyncThunk(
  "otNote/fetchOtReservations",
  async (params, { rejectWithValue }) => {
    try {
      const rows = await listOtReservations(params);
      return rows.map(mapReservationToTableRow);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchOtLookups = createAsyncThunk(
  "otNote/fetchOtLookups",
  async (_, { rejectWithValue }) => {
    try {
      const [theatres, anaesthesiaTypes, otResults, surgeries] = await Promise.all([
        listOtTheatres(),
        listAnaesthesiaTypes(),
        listOtResults(),
        searchOtSurgeries("", 100),
      ]);
      return {
        theatres: (theatres || []).map(toLookupOption),
        anaesthesiaTypes: (anaesthesiaTypes || []).map(toLookupOption),
        otResults: (otResults || []).map(toLookupOption),
        surgeries: (surgeries || []).map((s) => {
          const opt = toLookupOption(s);
          return {
            ...opt,
            label: opt.label,
            data: { code: String(opt.id), name: opt.label, surgeryId: opt.id },
          };
        }),
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchOtTables = createAsyncThunk(
  "otNote/fetchOtTables",
  async (otId, { rejectWithValue }) => {
    try {
      const tables = await listOtTables(otId);
      return (tables || []).map(toLookupOption);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const searchOtSurgeryOptions = createAsyncThunk(
  "otNote/searchOtSurgeryOptions",
  async (query, { rejectWithValue }) => {
    try {
      const surgeries = await searchOtSurgeries(query, 50);
      return (surgeries || []).map((s) => {
        const opt = toLookupOption(s);
        return {
          ...opt,
          data: { code: String(opt.id), name: opt.label, surgeryId: opt.id },
        };
      });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchOtNotesForVisit = createAsyncThunk(
  "otNote/fetchOtNotesForVisit",
  async ({ opdIpdId, opdIpd }, { rejectWithValue }) => {
    try {
      return await listOtNotesByVisit(opdIpdId, opdIpd);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchOtNoteById = createAsyncThunk(
  "otNote/fetchOtNoteById",
  async (operationDetailsId, { rejectWithValue }) => {
    try {
      return await getOtNoteById(operationDetailsId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const saveOtNote = createAsyncThunk(
  "otNote/saveOtNote",
  async ({ operationDetailsId, payload }, { rejectWithValue }) => {
    try {
      if (operationDetailsId) {
        return await updateOtNote(operationDetailsId, payload);
      }
      return await createOtNote(payload);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const otNoteSlice = createSlice({
  name: "otNote",
  initialState,
  reducers: {
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
    setSelectedPatient: (state, action) => {
      state.selectedPatient = action.payload;
    },
    setOperationDetailsId: (state, action) => {
      state.operationDetailsId = action.payload;
    },
    setOtReservationContext: (state, action) => {
      state.otReservationId = action.payload?.otReservationId ?? null;
      state.isOtReserved = Boolean(action.payload?.isOtReserved);
    },
    clearOtNoteError: (state) => {
      state.error = null;
    },
    resetOtNote: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOtReservations.pending, (state) => {
        state.reservationsLoading = true;
        state.error = null;
      })
      .addCase(fetchOtReservations.fulfilled, (state, action) => {
        state.reservationsLoading = false;
        state.reservations = action.payload || [];
      })
      .addCase(fetchOtReservations.rejected, (state, action) => {
        state.reservationsLoading = false;
        state.error = action.payload || "Failed to load reservations";
        state.reservations = [];
      })
      .addCase(fetchOtLookups.pending, (state) => {
        state.lookupsLoading = true;
      })
      .addCase(fetchOtLookups.fulfilled, (state, action) => {
        state.lookupsLoading = false;
        state.theatres = action.payload.theatres || [];
        state.anaesthesiaTypes = action.payload.anaesthesiaTypes || [];
        state.otResults = action.payload.otResults || [];
        state.surgeries = action.payload.surgeries || [];
      })
      .addCase(fetchOtLookups.rejected, (state, action) => {
        state.lookupsLoading = false;
        state.error = action.payload || "Failed to load OT lookups";
      })
      .addCase(fetchOtTables.fulfilled, (state, action) => {
        state.tables = action.payload || [];
      })
      .addCase(searchOtSurgeryOptions.fulfilled, (state, action) => {
        state.surgeries = action.payload || [];
      })
      .addCase(fetchOtNotesForVisit.fulfilled, (state, action) => {
        state.noteList = action.payload || [];
      })
      .addCase(fetchOtNoteById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOtNoteById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentNote = action.payload;
        state.operationDetailsId = action.payload?.note?.operationDetailsId ?? null;
        state.isOtReserved = Boolean(action.payload?.note?.isOtReserved);
      })
      .addCase(fetchOtNoteById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load OT note";
      })
      .addCase(saveOtNote.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveOtNote.fulfilled, (state, action) => {
        state.saving = false;
        if (action.payload?.operationDetailsId) {
          state.operationDetailsId = action.payload.operationDetailsId;
        }
      })
      .addCase(saveOtNote.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload || "Failed to save OT note";
      });
  },
});

export const {
  setActiveTab,
  setSelectedPatient,
  setOperationDetailsId,
  setOtReservationContext,
  clearOtNoteError,
  resetOtNote,
} = otNoteSlice.actions;

export default otNoteSlice.reducer;
